<script setup lang="ts">
import type { VTableFilterConfig } from "../../composables/Table/useTableFilters";
import type { ActiveFilter, TableFilterOption } from "../../types/TableFilter.types";
import { computed, onMounted, ref, useId } from "vue";
import { useI18n } from "vue-i18n";
import { booleanFilterLabel } from "../../composables/Table/useFilterSummary";
import { defaultConditionOf, useTableFilters } from "../../composables/Table/useTableFilters";
import { TableFilterCondition, TableFilterType } from "../../types/TableFilter.types";
import VCheckbox from "../Checkbox/VCheckbox.vue";
import VInput from "../Input/VInput.vue";
import VRadio from "../Radio/VRadio.vue";
import VSelectButton from "../SelectButton/VSelectButton.vue";
import VSkeleton from "../Skeleton/VSkeleton.vue";
import DateRangeFilter from "./filters/DateRangeFilter.vue";
import FilterConditionSelect from "./filters/FilterConditionSelect.vue";
import NumberRangeFilter from "./filters/NumberRangeFilter.vue";
import TextFilter from "./filters/TextFilter.vue";

defineOptions({
  name: "VTableFilterEditor",
});

/**
 * The controls of one filter, inline: a list of checkboxes for a multiple choice, radios for a single choice,
 * Alle / Ja / Nee for a yes-no filter, and the condition editors for text, numbers and dates. Used by the filter
 * panel, a filter button in the toolbar and the filter of a column. It edits a draft; the parent applies it.
 */
const { config, labelId } = defineProps<{
  config: VTableFilterConfig
  /** Id of the element with the filter's name; every control is named after it. */
  labelId: string
}>();

type FilterDraft = Pick<ActiveFilter, "condition" | "value">;

const draft = defineModel<FilterDraft>({ required: true });

const { t } = useI18n();
const { optionsOf, loadOptions, isOptionsLoading, isOptionsFailed } = useTableFilters();
const uid = useId();

/** More options than this get a search field above the list. */
const SEARCH_THRESHOLD = 6;

const hasOptions = computed(() =>
  config.type === TableFilterType.SELECT || config.type === TableFilterType.MULTI_SELECT,
);
const options = computed<TableFilterOption[]>(() => optionsOf(config.field));
const loadingOptions = computed(() => isOptionsLoading(config.field));
const failedOptions = computed(() => isOptionsFailed(config.field));

onMounted(() => {
  if (hasOptions.value) {
    void loadOptions(config.field).catch(() => {
      // The failure is shown in the list with a retry button.
    });
  }
});

function retryOptions() {
  void loadOptions(config.field, { force: true }).catch(() => {});
}

/** Only an explicit list of conditions in the config shows a condition choice for a choice filter. */
const choiceConditions = computed(() => {
  if (!hasOptions.value || !config.conditions || config.conditions.length < 2) {
    return [];
  }
  return config.conditions.map(condition => ({ value: condition, name: conditionName(condition) }));
});

function conditionName(condition: TableFilterCondition): string {
  const keys: Partial<Record<TableFilterCondition, string>> = {
    [TableFilterCondition.EQUALS]: "filters.conditions.equals",
    [TableFilterCondition.NOT_EQUALS]: "filters.conditions.not_equals",
    [TableFilterCondition.CONTAINS]: "filters.conditions.contains",
    [TableFilterCondition.NOT_CONTAINS]: "filters.conditions.not_contains",
    [TableFilterCondition.IS_NULL]: "filters.conditions.is_null",
    [TableFilterCondition.IS_NOT_NULL]: "filters.conditions.is_not_null",
  };
  const key = keys[condition];
  return key ? t(key) : condition;
}

const isNullCondition = computed(() =>
  draft.value.condition === TableFilterCondition.IS_NULL || draft.value.condition === TableFilterCondition.IS_NOT_NULL,
);

function setCondition(condition: TableFilterCondition) {
  draft.value = { ...draft.value, condition };
}

const query = ref("");
const showSearch = computed(() => options.value.length > SEARCH_THRESHOLD);
const visibleOptions = computed(() => {
  const term = query.value.trim().toLocaleLowerCase();
  if (!term) {
    return options.value;
  }
  return options.value.filter(option => option.label.toLocaleLowerCase().includes(term));
});

// Multiple choice ------------------------------------------------------------------------------------------------
const chosenValues = computed<string[]>(() =>
  Array.isArray(draft.value.value) ? (draft.value.value as Array<string | number>).map(String) : [],
);

function isChosen(option: TableFilterOption): boolean {
  return chosenValues.value.includes(String(option.value));
}

function toggleOption(option: TableFilterOption, checked: boolean) {
  const value = String(option.value);
  const next = checked
    ? [...chosenValues.value.filter(entry => entry !== value), value]
    : chosenValues.value.filter(entry => entry !== value);
  draft.value = {
    condition: draft.value.condition ?? defaultConditionOf(config),
    value: next.length > 0 ? next : null,
  };
}

// Single choice --------------------------------------------------------------------------------------------------
const radioName = `${uid}-choice`;
const singleValue = computed(() => {
  const value = draft.value.value;
  return value === null || value === undefined ? null : String(value);
});

function pickOption(option: TableFilterOption) {
  draft.value = {
    condition: isNullCondition.value ? TableFilterCondition.EQUALS : draft.value.condition,
    value: option.value as ActiveFilter["value"],
  };
}

function clearSingle() {
  draft.value = { condition: defaultConditionOf(config), value: null };
}

// Yes / no -------------------------------------------------------------------------------------------------------
type BooleanChoice = "all" | "yes" | "no";
const booleanItems = computed(() => [
  { value: "all" as const, label: t("filters.summary.all") },
  { value: "yes" as const, label: booleanFilterLabel(config, true) ?? t("filters.true") },
  { value: "no" as const, label: booleanFilterLabel(config, false) ?? t("filters.false") },
]);
const booleanChoice = computed<BooleanChoice>({
  get: () => {
    if (draft.value.value === true) {
      return "yes";
    }
    if (draft.value.value === false) {
      return "no";
    }
    return "all";
  },
  set: (choice) => {
    if (choice === "all") {
      draft.value = { condition: TableFilterCondition.IS_TRUE, value: null };
      return;
    }
    const yes = choice === "yes";
    draft.value = { condition: yes ? TableFilterCondition.IS_TRUE : TableFilterCondition.IS_FALSE, value: yes };
  },
});

// Text, number and date editors keep their own condition select. -------------------------------------------------
function setValue(value: unknown) {
  draft.value = { ...draft.value, value: value as ActiveFilter["value"] };
}

const helpId = `${uid}-help`;
const describedBy = computed(() => (config.help ? helpId : undefined));
</script>

<template>
  <div class="flex flex-col gap-2">
    <FilterConditionSelect
      v-if="choiceConditions.length > 0"
      :model-value="draft.condition"
      :options="choiceConditions"
      :label-id="labelId"
      @update:model-value="setCondition"
    />

    <template v-if="hasOptions && !isNullCondition">
      <div v-if="loadingOptions && options.length === 0" class="flex flex-col gap-2 px-2 py-1" aria-busy="true">
        <span class="sr-only">{{ t("base.loading") }}</span>
        <VSkeleton v-for="n in 3" :key="n" :height="14" :width="60" :dynamic-width="false" />
      </div>
      <div v-else-if="failedOptions && options.length === 0" class="flex flex-col items-start gap-1 px-2 py-1" role="alert">
        <span class="text-xs text-danger-700 dark:text-danger-400">{{ t("filters.options_failed") }}</span>
        <button
          type="button"
          class="h-7 cursor-pointer rounded-full px-2 text-xs font-medium text-primary-700 hover:bg-gray-100 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500 dark:text-primary-400 dark:hover:bg-dark-700"
          @click="retryOptions"
        >
          {{ t("filters.retry") }}
        </button>
      </div>
      <template v-else>
        <div v-if="showSearch" class="pt-1">
          <VInput
            v-model="query"
            type="search"
            size="medium"
            icon-left="search"
            :aria-label="t('filters.search_options', { label: config.label })"
            :placeholder="t('filters.search_options', { label: config.label })"
          />
        </div>

        <div
          v-if="config.type === TableFilterType.MULTI_SELECT"
          role="group"
          :aria-labelledby="labelId"
          :aria-describedby="describedBy"
          class="flex flex-col gap-0.5"
        >
          <VCheckbox
            v-for="option in visibleOptions"
            :key="String(option.value)"
            :model-value="isChosen(option)"
            :name="`${uid}-${String(option.value)}`"
            :label-right="option.label"
            class="min-h-9 rounded-md px-2 hover:bg-gray-100 dark:hover:bg-dark-700"
            @update:model-value="(checked: unknown) => toggleOption(option, Boolean(checked))"
          />
        </div>

        <div
          v-else
          role="radiogroup"
          :aria-labelledby="labelId"
          :aria-describedby="describedBy"
          class="flex flex-col gap-0.5"
        >
          <VRadio
            v-for="option in visibleOptions"
            :key="String(option.value)"
            :model-value="singleValue"
            :value="String(option.value)"
            :name="radioName"
            :label-right="option.label"
            class="min-h-9 rounded-md px-2 hover:bg-gray-100 dark:hover:bg-dark-700"
            @update:model-value="pickOption(option)"
          />
          <button
            type="button"
            class="mt-0.5 h-7 w-fit cursor-pointer rounded-full px-2 text-xs font-medium text-primary-700 hover:bg-gray-100 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500 disabled:pointer-events-none disabled:opacity-40 dark:text-primary-400 dark:hover:bg-dark-700"
            :disabled="singleValue === null"
            @click="clearSingle"
          >
            {{ t("filters.clear_filter") }}
          </button>
        </div>

        <span v-if="visibleOptions.length === 0" class="px-2 py-1 text-xs text-gray-600 dark:text-gray-400" role="status">
          {{ options.length === 0 ? t("filters.no_options") : t("filters.no_option_matches") }}
        </span>
      </template>
    </template>

    <VSelectButton
      v-else-if="config.type === TableFilterType.BOOLEAN"
      v-model="booleanChoice"
      :options="booleanItems"
      option-label="label"
      option-value="value"
      block
      :aria-labelledby="labelId"
      :aria-describedby="describedBy"
    />

    <TextFilter
      v-else-if="config.type === TableFilterType.TEXT"
      :model-value="(draft.value as string | null)"
      :condition="draft.condition"
      :placeholder="t('filters.value_placeholder')"
      :label-id="labelId"
      @update:model-value="setValue"
      @update:condition="setCondition"
    />

    <NumberRangeFilter
      v-else-if="config.type === TableFilterType.NUMBER_RANGE"
      :model-value="(draft.value as unknown as [number, number] | number | null)"
      :condition="draft.condition"
      :placeholder="t('filters.value_placeholder')"
      :label-id="labelId"
      @update:model-value="setValue"
      @update:condition="setCondition"
    />

    <DateRangeFilter
      v-else-if="config.type === TableFilterType.DATE_RANGE"
      :model-value="(draft.value as unknown as [Date, Date] | Date | null)"
      :condition="draft.condition"
      :placeholder="t('filters.date_placeholder')"
      :label-id="labelId"
      @update:model-value="setValue"
      @update:condition="setCondition"
    />

    <p v-if="config.help" :id="helpId" class="m-0 px-2 text-xs text-gray-600 dark:text-gray-400">
      {{ config.help }}
    </p>
  </div>
</template>
